import styled from '@emotion/styled';
import { EnHeading } from 'components/atoms/enHeading';
import { ProjectCard } from 'components/molecules/projectCard';
import { ContentWrapper } from 'components/organisms/contentWrapper';
import FadeUpItem from 'components/organisms/fadeUpItem';
import FadeUpSection from 'components/organisms/fadeUpSection';
import InquiryBlock from 'components/organisms/inquiryBlock/';
import ParticleImageManager from 'components/organisms/particleCanvas/ParticleImageManager';
import ProjectCardCol from 'components/organisms/projectCardCol';
import { graphql } from 'gatsby';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import sizes from 'style/sizes';
import { breakpoint, mq } from 'style/variables';
import SEO from 'util/seo';
import { translateText } from 'util/translator';

interface IndexPageProps {
  data: any;
}

export const DISPLAY_COUNT_OF_PICKUP_PROJECTS = 2;

const TitleSection = styled('div')`
  width: 100%;
  ${mq.onlypc} {
    padding-top: ${sizes.margin[180]};
    padding-bottom: ${sizes.margin[100]};
  }
  ${mq.onlysp} {
    padding-top: ${sizes.margin[155]};
    padding-bottom: ${sizes.margin[64]};
    padding-left: ${sizes.margin[32]};
    width: unset;
  }
`;

const ProjectArea = styled.div`
  display: grid;
  gap: ${sizes.margin[120]};
  ${mq.onlysp} {
    gap: ${sizes.margin[48]};
  }
`;

const categories = [
  'ALL',
  'SERVICE DESIGN',
  'DIGITAL PRODUCT',
  'DIGITAL BRANDING',
  'EXPERIENCE DESIGN',
  'DESIGN OPS',
] as const;

type CategoryType = (typeof categories)[number];

const categorySlugMap: Record<CategoryType, string | null> = {
  ALL: null,
  'SERVICE DESIGN': 'service-design',
  'DIGITAL PRODUCT': 'digital-product',
  'DIGITAL BRANDING': 'digital-branding',
  'EXPERIENCE DESIGN': 'experience-design',
  'DESIGN OPS': 'design-ops',
};

const Pages: React.FC<IndexPageProps> = ({ data }) => {
  const [category, setCategory] = useState<CategoryType>('ALL');

  const projectsOldLayout = data.allProjectsJson.edges; // projects.json
  const projectsNewLayout = data.allProjectsJpJson.edges; // projects_jp.json
  const projects = projectsNewLayout.concat(projectsOldLayout);
  const pickups = data.allPickupProjectsJson.edges?.[0]?.node?.acf?.pickup;

  const handleCategory = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setCategory(e.currentTarget.textContent as CategoryType);
  }, []);

  const selectedProjects = useMemo(() => {
    if (category === 'ALL') {
      return projects;
    } else {
      const slug = categorySlugMap[category];
      return projects?.filter((project: any) =>
        project.node?.acf?.services?.some((service: any) => service?.post_name === slug)
      );
    }
  }, [category, projects]);

  const pickUpProjects = useMemo(() => {
    if (category === 'ALL') {
      return pickups
        ?.map((pickup: any) =>
          projects?.filter((project: any) => project?.node?.title?.rendered === pickup?.post_title)
        )
        .flat()
        .slice(0, DISPLAY_COUNT_OF_PICKUP_PROJECTS);
    } else {
      return selectedProjects
        ?.filter((project: any) => project?.node.acf?.pickup)
        .slice(0, DISPLAY_COUNT_OF_PICKUP_PROJECTS);
    }
  }, [selectedProjects, pickups, projects]);

  const listProjects = useMemo(() => {
    return selectedProjects?.filter((project: any) => !pickUpProjects?.includes(project));
  }, [selectedProjects, pickUpProjects]);

  const showProjects = listProjects;

  let isSp = false;
  if (typeof window !== 'undefined') isSp = window.innerWidth <= breakpoint;

  useEffect(() => {
    ParticleImageManager.loadAll().then(() => {
      ParticleImageManager.generateAll().then(() => {
        ParticleImageManager.start();
      });
    });

    return () => {
      ParticleImageManager.delete();
    };
  }, []);

  return (
    <>
      <SEO title={translateText('projects.title') || 'Projects'} />
      <ContentWrapper noSpPadding>
        <TitleSection>
          <EnHeading>{translateText('projects.title') || 'Projects'}</EnHeading>
        </TitleSection>
        <ProjectArea>
          <FadeUpItem delay={0.6}>
            <ProjectCardCol col={2}>
              {pickUpProjects?.map((project: any, idx: number) => {
                return (
                  <ProjectCard
                    key={`pickup-th-${category}-${project?.node?.id}-${idx}`}
                    size={'large'}
                    spSize="small"
                    imgSpSize="full"
                    imgpath={project?.node?.acf?.thumb || `noimage/img-card-thumb.jpg`}
                    title={
                      project?.node?.title?.rendered ||
                      project?.node?.acf?.card_headline_en ||
                      project?.node?.acf?.page_headline_en ||
                      'NO TITLE'
                    }
                    client={
                      translateText(project?.node?.acf?.client_abbr?.ja) ||
                      project?.node?.acf?.client_abbr?.en ||
                      undefined
                    }
                    work={project?.node?.acf?.product?.en || undefined}
                    tags={project?.node?.acf?.outline as string[]}
                    to={`/th/projects/${project?.node?.slug}`}
                    delay={idx + 1}
                  />
                );
              })}
            </ProjectCardCol>
          </FadeUpItem>
          <FadeUpSection>
            <ProjectCardCol col={3}>
              {showProjects?.map((project: any, idx: number) => {
                return (
                  <ProjectCard
                    key={`list-th-${category}-${project.node.id}-${idx}`}
                    size={'small'}
                    spSize="small"
                    imgpath={project?.node?.acf?.thumb || `noimage/img-card-thumb.jpg`}
                    title={
                      project?.node?.title?.rendered ||
                      project?.node?.acf?.card_headline_en ||
                      project?.node?.acf?.page_headline_en ||
                      'NO TITLE'
                    }
                    client={
                      translateText(project?.node?.acf?.client_abbr?.ja) ||
                      project?.node?.acf?.client_abbr?.en ||
                      undefined
                    }
                    work={project?.node?.acf?.product?.en || undefined}
                    tags={project?.node?.acf?.outline as string[]}
                    to={`/th/projects/${project.node.slug}`}
                    delay={idx + 1}
                  />
                );
              })}
            </ProjectCardCol>
          </FadeUpSection>
        </ProjectArea>
      </ContentWrapper>

      <InquiryBlock th />
    </>
  );
};

export const pageQuery = graphql`
  query ProjectsIndexTh {
    site {
      siteMetadata {
        title
      }
    }
    allPickupProjectsJson(filter: { title: { rendered: { eq: "allpickupprojects" } } }) {
      edges {
        node {
          acf {
            pickup {
              post_title
            }
          }
        }
      }
    }
    allProjectsJpJson(sort: { fields: date, order: DESC }, filter: { title: { rendered: { nin: ["dummy", null] } } }) {
      edges {
        node {
          id
          title {
            rendered
          }
          slug
          acf {
            page_headline_ja
            card_headline_ja
            page_headline_en
            card_headline_en
            headline_en
            pickup
            client_abbr {
              en
              ja
            }
            services {
              post_title
              post_name
            }
            launch
            thumb
            pickup
            outline
            kv
            kv_sp
            product {
              ja
              en
            }
          }
        }
      }
    }
    allProjectsJson(sort: { fields: date, order: DESC }, filter: { title: { rendered: { nin: ["dummy", null] } } }) {
      edges {
        node {
          id
          title {
            rendered
          }
          slug
          acf {
            page_headline_ja
            card_headline_ja
            headline_en
            client_abbr {
              en
              ja
            }
            services {
              post_title
              post_name
            }
            launch
            thumb
            pickup
            outline
            kv
            kv_sp
            product {
              ja
              en
            }
          }
        }
      }
    }
  }
`;

export default Pages;
